import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Image as ImageIcon, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/pages/admin/ConfirmDelete";

const empty = { title: "", category: "", description: "", story: "", gallery: [], url: "", order: 0 };
const STORY_PLACEHOLDER = "## The Problem\nWhat problem does this solve, and for whom?\n\n## What It Does\nA clear description of the project.\n\n## Why I Built It\nThe motivation behind building it.";

export default function AdminProjects() {
  const [projects, setProjects] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const galleryInputRef = useRef(null);

  const load = () => api.get("/projects").then(setProjects).catch(() => toast.error("Couldn't load projects."));
  useEffect(() => { load(); }, []);

  const set = (field) => (e) => setEditing({ ...editing, [field]: e.target.value });

  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingImg(true);
    try {
      const data = await api.upload("/upload", file);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setEditing((prev) => ({ ...prev, gallery: [...(prev.gallery || []), fullUrl] }));
      toast.success("Screenshot added.");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't upload the screenshot. Please try again."));
    } finally {
      setUploadingImg(false);
    }
  };

  const removeGalleryImage = (idx) => {
    setEditing((prev) => ({ ...prev, gallery: prev.gallery.filter((_, i) => i !== idx) }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...editing, order: Number(editing.order) || 0, title: editing.title.trim(), url: editing.url.trim(), description: editing.description.trim(), category: editing.category.trim(), story: (editing.story || "").trim(), gallery: editing.gallery || [] };
    try {
      if (editing.id) await api.put(`/projects/${editing.id}`, body);
      else await api.post("/projects", body);
      toast.success(editing.id ? "Project updated." : "Project added.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.del(`/projects/${id}`);
      toast.success("Project deleted.");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-base text-stone-600">{projects ? `${projects.length} project${projects.length === 1 ? "" : "s"}` : "Loading..."}</p>
        <Button onClick={() => setEditing({ ...empty })} data-testid="admin-new-project-button" className="rounded-full bg-amber-600 font-semibold text-white hover:bg-amber-700 gap-2">
          <Plus className="h-4 w-4" /> Add project
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects?.length === 0 && <p className="text-stone-500" data-testid="admin-projects-empty">No projects yet.</p>}
        {projects?.map((p) => (
          <div key={p.id} className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm" data-testid={`admin-project-card-${p.id}`}>
            <p className="font-mono text-xs uppercase tracking-widest text-amber-700">{p.category || "Project"}</p>
            <h3 className="mt-2 font-heading text-lg font-semibold text-stone-900">{p.title}</h3>
            <p className="mt-2 text-sm text-stone-600 leading-relaxed line-clamp-3">{p.description}</p>
            <a href={p.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-stone-400 hover:text-amber-700">
              {p.url.replace(/^https?:\/\//, "").split("/")[0]} <ExternalLink className="h-3 w-3" />
            </a>
            <div className="mt-5 flex items-center gap-2">
              <button type="button" onClick={() => setEditing({ ...p })} data-testid={`admin-project-edit-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:border-stone-900">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <ConfirmDelete title={`Delete "${p.title}"?`} onConfirm={() => remove(p.id)} triggerTestId={`admin-project-delete-${p.id}`}>
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </ConfirmDelete>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.id ? "Edit project" : "Add project"}</DialogTitle>
            <DialogDescription>Fill in the project details and an optional write-up shown when expanded on the public Projects page.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4" data-testid="project-form">
              <div className="space-y-1.5">
                <Label htmlFor="project-title">Name</Label>
                <Input id="project-title" required value={editing.title} onChange={set("title")} data-testid="project-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="project-category">Category / tagline</Label>
                <Input id="project-category" value={editing.category} onChange={set("category")} placeholder="Tax & Travel" data-testid="project-category-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="project-description">Description (short, shown on the card)</Label>
                <Textarea id="project-description" required rows={2} value={editing.description} onChange={set("description")} data-testid="project-description-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="project-story">Full write-up (Markdown — shown when expanded)</Label>
                <Textarea
                  id="project-story"
                  rows={9}
                  value={editing.story || ""}
                  onChange={set("story")}
                  placeholder={STORY_PLACEHOLDER}
                  data-testid="project-story-input"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-stone-400">Tip: use "## The Problem", "## What It Does" and "## Why I Built It" headings for a clear, scannable write-up.</p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Screenshot gallery</Label>
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="hidden"
                    onChange={handleGalleryUpload}
                    data-testid="project-gallery-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingImg}
                    onClick={() => galleryInputRef.current?.click()}
                    data-testid="project-gallery-upload-button"
                    className="gap-2 rounded-full text-xs font-semibold"
                  >
                    <ImageIcon className="h-3.5 w-3.5" /> {uploadingImg ? "Uploading..." : "Add screenshot"}
                  </Button>
                </div>
                {editing.gallery?.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2" data-testid="project-gallery-preview">
                    {editing.gallery.map((src, idx) => (
                      <div key={idx} className="relative group">
                        <img src={src} alt={`Screenshot ${idx + 1}`} className="aspect-video w-full rounded-lg object-cover border border-stone-200" />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(idx)}
                          data-testid={`project-gallery-remove-${idx}`}
                          className="absolute -top-1.5 -right-1.5 rounded-full bg-stone-900 p-1 text-white opacity-100 sm:opacity-0 transition-opacity sm:group-hover:opacity-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-[1fr_90px] gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="project-url">Link URL</Label>
                  <Input id="project-url" type="url" required value={editing.url} onChange={set("url")} placeholder="https://" data-testid="project-url-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="project-order">Order</Label>
                  <Input id="project-order" type="number" value={editing.order} onChange={set("order")} data-testid="project-order-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="project-save-button" className="rounded-full bg-amber-600 font-semibold text-white hover:bg-amber-700">
                  {saving ? "Saving..." : "Save project"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
